import { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowDown } from 'lucide-react';
import { useI18n } from '@/lib/i18n';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import heroBg from '@/assets/hero-bg.jpg';

const WORD_DELAY = 0.15;
const HOLD_DURATION = 2000;
const FADE_OUT_DURATION = 0.4;

const fallbackRu = ['От идеи к образу.', 'Арт-дирекшн, основанный на концепции.'];
const fallbackEn = ['From idea to image.', 'Concept-driven art direction.'];

const RotatingHeadline = () => {
  const { lang } = useI18n();

  const { data: settings } = useQuery({
    queryKey: ['site-settings'],
    queryFn: async () => {
      const { data } = await supabase.from('site_settings').select('*').maybeSingle();
      return data;
    },
  });

  const phrases = lang === 'ru'
    ? (Array.isArray(settings?.hero_phrases_ru) && settings.hero_phrases_ru.length > 0 ? settings.hero_phrases_ru as string[] : fallbackRu)
    : (Array.isArray(settings?.hero_phrases_en) && settings.hero_phrases_en.length > 0 ? settings.hero_phrases_en as string[] : fallbackEn);

  const [index, setIndex] = useState(0);
  const [visible, setVisible] = useState(true);

  const words = phrases[index % phrases.length].split(' ');
  const appearDuration = words.length * WORD_DELAY * 1000 + 400;

  const cycle = useCallback(() => {
    setVisible(false);
    setTimeout(() => {
      setIndex((i) => (i + 1) % phrases.length);
      setVisible(true);
    }, FADE_OUT_DURATION * 1000 + 200);
  }, [phrases.length]);

  useEffect(() => {
    const timer = setTimeout(cycle, appearDuration + HOLD_DURATION);
    return () => clearTimeout(timer);
  }, [index, visible, cycle, appearDuration]);

  return (
    <div className="h-[1.2em] flex items-center justify-center">
      <AnimatePresence mode="wait">
        {visible && (
          <motion.h1
            key={index}
            initial={{ opacity: 1 }}
            exit={{ opacity: 0, transition: { duration: FADE_OUT_DURATION } }}
            className="text-5xl md:text-7xl lg:text-7xl font-display font-bold leading-[1.1] tracking-tight text-[#f3f6f7] uppercase"
          >
            {words.map((word, i) => (
              <motion.span
                key={i}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * WORD_DELAY, duration: 0.35, ease: 'easeOut' }}
                className="inline-block mr-[0.3em]"
              >
                {word}
              </motion.span>
            ))}
          </motion.h1>
        )}
      </AnimatePresence>
    </div>
  );
};

const Hero = () => {
  const { data: settings, isLoading } = useQuery({
    queryKey: ['site-settings'],
    queryFn: async () => {
      const { data } = await supabase.from('site_settings').select('*').maybeSingle();
      return data;
    },
  });

  const videoUrl = settings?.hero_video_url;

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
      {isLoading ? (
        <div className="absolute inset-0 bg-black" />
      ) : videoUrl ? (
        <video
          src={videoUrl}
          autoPlay
          loop
          muted
          playsInline
          className="absolute inset-0 w-full h-full object-cover scale-120"
        />
      ) : (
        <img src={heroBg} alt="" className="absolute inset-0 w-full h-full object-cover scale-120" />
      )}

      <div className="absolute inset-0 bg-black/65 z-[1]" />

      <div className="container relative z-10 pt-20">
        <div className="flex flex-col items-center text-center">
          <div className="mb-12">
            <RotatingHeadline />
          </div>
        </div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.2 }}
          className="absolute -bottom-20 left-1/2 -translate-x-1/2"
        >
          <motion.div
            animate={{ y: [0, 8, 0] }}
            transition={{ duration: 2, repeat: Infinity }}
          >
            <ArrowDown className="text-muted-foreground" size={20} />
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

export default Hero;
